import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  BarChart2,
  RefreshCw,
  FileText,
  FileSpreadsheet,
  IndianRupee,
  TrendingUp,
  Users,
  Receipt,
  CheckCircle,
  AlertCircle,
  Loader2,
  CalendarDays,
  BadgeCheck,
  ArrowLeft,
} from "lucide-react";
import http from "../../utils/axios";
import { config } from "../../utils/config";
import { showApiError, showApiSuccess } from "../../utils/toast";
import Pagination from "../../components/tables/Pagination";
import DateRangePicker from "../../components/ui/DateRangePicker";

// ─── constants ───────────────────────────────────────────────────────────────

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const now = new Date();
const currentYear = now.getFullYear();
const currentMonth = now.getMonth() + 1;

const YEARS = Array.from(
  { length: currentYear - 2023 },
  (_, i) => currentYear - i
);

const getAvailableMonths = (year) => {
  if (year < currentYear) return MONTHS;
  if (year === currentYear) return MONTHS.filter((m) => m.value <= currentMonth);
  return [];
};

// ─── helpers ────────────────────────────────────────────────────────────────

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount || 0);

const formatDate = (dateString) =>
  new Date(dateString).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const getDefaultDateRange = (year, month) => {
  const monthStart = new Date(Date.UTC(year, month - 1, 1));
  const monthEnd = new Date(Date.UTC(year, month, 0));
  const fmt = (d) => `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
  return { from_date: fmt(monthStart), to_date: fmt(monthEnd) };
};

// ─── sub-components ─────────────────────────────────────────────────────────

function SummaryCard({ icon: Icon, label, value, iconBg, textColor }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 flex items-start space-x-3">
      <div className={`${iconBg} p-2.5 rounded-lg flex-shrink-0`}>
        <Icon className={`h-5 w-5 ${textColor}`} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-gray-500 font-medium truncate">{label}</p>
        <p className={`text-lg font-bold ${textColor} mt-0.5 truncate`}>{value}</p>
      </div>
    </div>
  );
}

function SettlementBadge({ status }) {
  const isSettled = status === "Settled";
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${
      isSettled
        ? "bg-green-50 text-green-700 border-green-200"
        : "bg-yellow-50 text-yellow-700 border-yellow-200"
    }`}>
      {isSettled ? <BadgeCheck className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
      {status}
    </span>
  );
}

// ─── main page ───────────────────────────────────────────────────────────────

export default function AdminBusinessTransactions() {
  const { businessUuid } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── State ──────────────────────────────────────────────────────────────
  const [business, setBusiness] = useState(null);
  const [businessLoading, setBusinessLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  const defaultDateRange = getDefaultDateRange(currentYear, currentMonth);
  const pageFromUrl = parseInt(searchParams.get("page") || "1", 10);

  const [filters, setFilters] = useState({
    year: currentYear,
    month: currentMonth,
    from_date: defaultDateRange.from_date,
    to_date: defaultDateRange.to_date,
    page: pageFromUrl,
    per_page: 15,
  });

  // Sync filters.page when URL changes externally
  useEffect(() => {
    if (pageFromUrl !== filters.page) {
      setFilters((prev) => ({ ...prev, page: pageFromUrl }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageFromUrl]);

  // ── Fetch business details ─────────────────────────────────────────────
  useEffect(() => {
    if (!businessUuid) return;
    setBusinessLoading(true);
    http.get(`/dashboard/businesses/${businessUuid}`)
      .then((res) => {
        setBusiness(res.data || res);
        setBusinessLoading(false);
      })
      .catch(() => {
        setBusiness(null);
        setBusinessLoading(false);
      });
  }, [businessUuid]);

  // ── Fetch report ───────────────────────────────────────────────────────
  const fetchReport = useCallback(async () => {
    if (!businessUuid) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append("year", filters.year);
      params.append("month", filters.month);
      params.append("business_uuid", businessUuid);
      if (filters.from_date) params.append("from_date", filters.from_date);
      if (filters.to_date) params.append("to_date", filters.to_date);
      params.append("per_page", filters.per_page);
      params.append("page", filters.page);

      const res = await http.get(
        `/dashboard/reports/businesses/${businessUuid}/transactions/summary?${params.toString()}`
      );
      const responseData = res?.data || res;
      setReport({
        data: responseData?.data || [],
        pagination: responseData?.pagination || null,
        filter: responseData?.filter || null,
        stats: responseData?.stats || null,
      });
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch report");
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [businessUuid, filters]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // ── Filter actions ─────────────────────────────────────────────────────
  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => {
      const updated = { ...prev, [key]: value };
      if (key !== "page") updated.page = 1;
      if (key === "year" || key === "month") {
        const newYear = key === "year" ? value : updated.year;
        const newMonth = key === "month" ? value : updated.month;
        const range = getDefaultDateRange(newYear, newMonth);
        updated.from_date = range.from_date;
        updated.to_date = range.to_date;
      }
      return updated;
    });
    if (key !== "page") {
      setSearchParams({ page: "1" });
    }
  }, [setSearchParams]);

  const handlePageChange = useCallback((page) => {
    setFilters((prev) => ({ ...prev, page }));
    setSearchParams({ page: String(page) });
  }, [setSearchParams]);

  // ── Export ─────────────────────────────────────────────────────────────
  const triggerDownload = (blobUrl, filename) => {
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  };

  const handleExportXlsx = async () => {
    setExportLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const params = new URLSearchParams();
      params.append("year", filters.year);
      params.append("month", filters.month);
      params.append("business_uuid", businessUuid);
      if (filters.from_date) params.append("from_date", filters.from_date);
      if (filters.to_date) params.append("to_date", filters.to_date);
      const url = `${config.API_BASE_URL}/dashboard/reports/businesses/${businessUuid}/transactions/export?${params.toString()}`;
      const response = await fetch(url, { method: "GET", headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error("Export failed");
      const blob = await response.blob();
      triggerDownload(URL.createObjectURL(blob), `transactions_${businessUuid}_${filters.year}_${filters.month}.xlsx`);
      showApiSuccess("XLSX downloaded successfully");
    } catch {
      showApiError(null, "Failed to export XLSX");
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportPdf = async () => {
    setExportLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const params = new URLSearchParams();
      params.append("year", filters.year);
      params.append("month", filters.month);
      params.append("business_uuid", businessUuid);
      if (filters.from_date) params.append("from_date", filters.from_date);
      if (filters.to_date) params.append("to_date", filters.to_date);
      const url = `${config.API_BASE_URL}/dashboard/reports/businesses/${businessUuid}/transactions/export-pdf?${params.toString()}`;
      const response = await fetch(url, { method: "GET", headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error("Export failed");
      const blob = await response.blob();
      triggerDownload(URL.createObjectURL(blob), `transactions_${businessUuid}_${filters.year}_${filters.month}.pdf`);
      showApiSuccess("PDF downloaded successfully");
    } catch {
      showApiError(null, "Failed to export PDF");
    } finally {
      setExportLoading(false);
    }
  };

  // ── Derived data ───────────────────────────────────────────────────────
  const transactions = Array.isArray(report?.data) ? report.data : [];
  const pagination = report?.pagination || null;
  const stats = report?.stats || null;
  const selectedMonthLabel = MONTHS.find((m) => m.value === filters.month)?.label || "";

  const summaryTotals = {
    totalBillAmount: transactions.reduce((sum, t) => sum + (t.bill_amount || 0), 0),
    totalCustomerDiscount: transactions.reduce((sum, t) => sum + (t.customer_discount || 0), 0),
    totalPlatformCommission: transactions.reduce((sum, t) => sum + (t.platform_commission || 0), 0),
    totalFinalAmount: transactions.reduce((sum, t) => sum + (t.final_amount || 0), 0),
  };

  const cards = [
    { icon: Receipt, label: "Total Transactions", value: stats?.total_transactions || 0, iconBg: "bg-blue-100", textColor: "text-blue-600" },
    { icon: IndianRupee, label: "Total Revenue", value: formatCurrency(stats?.total_revenue || 0), iconBg: "bg-purple-100", textColor: "text-purple-600" },
    { icon: Users, label: "Customer Savings", value: formatCurrency(stats?.customer_savings || 0), iconBg: "bg-green-100", textColor: "text-green-600" },
    { icon: TrendingUp, label: "Platform Commission", value: formatCurrency(stats?.platform_commission || 0), iconBg: "bg-orange-100", textColor: "text-orange-600" },
    { icon: CheckCircle, label: "Net Earnings", value: formatCurrency(stats?.net_earnings || 0), iconBg: "bg-emerald-100", textColor: "text-emerald-600" },
  ];

  if (businessLoading) {
    return (
      <div className="flex items-center justify-center py-20 gap-3 text-gray-500">
        <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
        <span>Loading…</span>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header with back button */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 sm:p-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/reports")}
            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <BarChart2 className="h-6 w-6 sm:h-7 sm:w-7 text-blue-600" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                {business?.business_name || "Business"} — Transactions
              </h1>
              <p className="text-xs sm:text-sm text-gray-500">
                {business?.email || ""}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {cards.map((card, index) => (
            <SummaryCard key={index} {...card} />
          ))}
        </div>
      )}

      {/* Table with filters */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-visible">
        {/* Filters */}
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 relative z-10">
          <div className="flex items-center gap-2 text-gray-600 mb-3">
            <CalendarDays className="h-4 w-4 flex-shrink-0" />
            <span className="text-xs font-medium uppercase whitespace-nowrap">Filter by:</span>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 mb-3">
            {/* Year */}
            <select
              value={filters.year}
              onChange={(e) => {
                const newYear = Number(e.target.value);
                updateFilter("year", newYear);
                const available = getAvailableMonths(newYear);
                if (!available.find((m) => m.value === filters.month)) {
                  updateFilter("month", available[available.length - 1].value);
                }
              }}
              className="flex-1 sm:flex-none px-3 py-2 border border-gray-300 rounded text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>

            {/* Month */}
            <select
              value={filters.month}
              onChange={(e) => updateFilter("month", Number(e.target.value))}
              className="flex-1 sm:flex-none px-3 py-2 border border-gray-300 rounded text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              {getAvailableMonths(filters.year).map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>

            {/* Date Range Picker */}
            <div className="flex-1 sm:flex-none">
              <DateRangePicker
                year={filters.year}
                month={filters.month}
                fromDate={filters.from_date}
                toDate={filters.to_date}
                onFromDateChange={(date) => updateFilter("from_date", date)}
                onToDateChange={(date) => updateFilter("to_date", date)}
              />
            </div>
          </div>

          {/* Row 2 - Reset & Export */}
          <div className="flex flex-wrap gap-2 items-center justify-between">
            <button
              onClick={() => {
                const range = getDefaultDateRange(filters.year, filters.month);
                setFilters((prev) => ({ ...prev, from_date: range.from_date, to_date: range.to_date, page: 1 }));
              }}
              className="px-3 py-2 rounded border border-gray-300 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Reset
            </button>

            <div className="flex items-center gap-2 ml-auto">
              <button
                onClick={handleExportXlsx}
                disabled={exportLoading || loading || transactions.length === 0}
                className="flex items-center gap-1.5 px-3 py-2 rounded border border-green-600 text-green-700 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs font-medium"
              >
                {exportLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
                <span className="hidden sm:inline">Excel</span>
              </button>

              <button
                onClick={handleExportPdf}
                disabled={exportLoading || loading || transactions.length === 0}
                className="flex items-center gap-1.5 px-3 py-2 rounded border border-red-500 text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs font-medium"
              >
                {exportLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                <span className="hidden sm:inline">PDF</span>
              </button>

              <button
                onClick={fetchReport}
                disabled={loading}
                title="Refresh"
                className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Table title */}
        <div className="px-4 sm:px-6 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-700">
            Transaction Details
            {pagination && <span className="ml-2 font-normal text-gray-500">({pagination.total} total)</span>}
          </h2>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-16 gap-3 text-gray-500">
            <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
            <span>Loading report…</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <AlertCircle className="h-10 w-10 text-red-400" />
            <p className="text-gray-600">{error}</p>
            <button onClick={fetchReport} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors">
              Try Again
            </button>
          </div>
        ) : transactions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <BarChart2 className="h-12 w-12 text-gray-300" />
            <p className="text-gray-500 font-medium">No transactions for {selectedMonthLabel} {filters.year}</p>
            <p className="text-gray-400 text-sm">Try selecting a different month or year.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {["#", "Reference ID", "Customer", "Mobile", "Bill Amount", "Customer Discount", "Final Amount", "Date & Time", "Platform Commission", "Settlement"].map((col) => (
                    <th key={col} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {transactions.map((t) => (
                  <tr key={t.reference_id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-gray-400 font-mono text-xs">{t.sl_no}</td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-blue-700 bg-blue-50 px-2 py-0.5 rounded">{t.reference_id}</span>
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-800 whitespace-nowrap">{t.customer_name || "N/A"}</td>
                    <td className="px-4 py-3 text-gray-600 font-mono">{t.mobile}</td>
                    <td className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">{formatCurrency(t.bill_amount)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-green-700 font-medium">{formatCurrency(t.customer_discount)}</span>
                      <span className="text-xs text-gray-400 ml-1">
                        ({t.bill_amount ? ((t.customer_discount / t.bill_amount) * 100).toFixed(1) : 0}%)
                      </span>
                    </td>
                    <td className="px-4 py-3 font-bold text-emerald-700 whitespace-nowrap">{formatCurrency(t.final_amount)}</td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">{formatDate(t.date_time)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-orange-600 font-medium">{formatCurrency(t.platform_commission)}</span>
                      <span className="text-xs text-gray-400 ml-1">
                        ({t.bill_amount ? ((t.platform_commission / t.bill_amount) * 100).toFixed(1) : 0}%)
                      </span>
                    </td>
                    <td className="px-4 py-3"><SettlementBadge status={t.settlement_status} /></td>
                  </tr>
                ))}
              </tbody>
              {transactions.length > 0 && (
                <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                  <tr>
                    <td colSpan={4} className="px-4 py-3 text-sm font-semibold text-gray-700">Page Total</td>
                    <td className="px-4 py-3 font-bold text-gray-900 whitespace-nowrap">{formatCurrency(summaryTotals.totalBillAmount)}</td>
                    <td className="px-4 py-3 font-bold text-green-700 whitespace-nowrap">{formatCurrency(summaryTotals.totalCustomerDiscount)}</td>
                    <td className="px-4 py-3 font-bold text-emerald-700 whitespace-nowrap">{formatCurrency(summaryTotals.totalFinalAmount)}</td>
                    <td colSpan={1} />
                    <td className="px-4 py-3 font-bold text-orange-600 whitespace-nowrap">{formatCurrency(summaryTotals.totalPlatformCommission)}</td>
                    <td colSpan={1} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.last_page > 1 && (
          <Pagination
            currentPage={pagination.current_page}
            totalPages={pagination.last_page}
            totalItems={pagination.total}
            itemsPerPage={pagination.per_page}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </div>
  );
}
